/**
 * Session query + admin context helpers.
 *
 * Replaces the old `<AdminProvider>` context. The current admin is
 * the response of `/api/admin/v1/me`, cached by TanStack Query with
 * `staleTime: Infinity`. Every component that needs the admin reads
 * from the cache via `useAdmin()` — no provider, no props drilling.
 *
 *   <AdminProvider> (old)  →  sessionQuery + useAdmin() (new)
 *
 * Permission checks go through `can(permission)` and `canWrite()`.
 * These delegate to `@pureid/ui`'s static permission catalog so the
 * SPA never hand-rolls permission strings.
 */

import { useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import type { z } from "zod";
import type { managedTenantContextSchema } from "@/lib/managed-contract";
import { tenantContextOptions } from "@/features/managed-administration/queries";
import { apiFetch, ApiError } from "@/lib/api-client";
import { PLATFORM_TENANT_SLUG } from "@/lib/platform-tenant";
import { queryKeys } from "@/lib/query-keys";

// ── Wire shape ────────────────────────────────────────────────────────────

export interface AdminSession {
  admin: {
    id: string;
    email: string;
    display_name: string | null;
    role: string;
    role_display_name?: string;
    tenant_id: string | null;
    tenant_slug: string | null;
    tenant_name: string | null;
    organization_id: string | null;
    organization_slug: string | null;
    organization_name: string | null;
    permissions: string[];
    created_at: string;
  };
  is_platform_admin: boolean;
  organizations_enabled: boolean;
}

export function sessionHasPermission(session: AdminSession | undefined, permission: string): boolean {
  const permissions = session?.admin.permissions ?? [];
  return permissions.includes(permission) || permissions.includes(`${permission}!`);
}

export const sessionQueryKey = queryKeys.session;

/**
 * Wire shape returned by the backend's HandleMe. It embeds the
 * AdminUser record fields at the top level and appends permissions
 * + role_display_name. The frontend's AdminSession shape is nested
 * (`{admin: {...}, is_platform_admin, organizations_enabled}`) so
 * we adapt here rather than scattering the mapping across every
 * call site.
 */
interface AdminMeWire {
  id: string;
  tenant_id: string | null;
  tenant_slug: string | null;
  tenant_name: string | null;
  organization_id?: string | null;
  organization_slug?: string | null;
  organization_name?: string | null;
  name: string | null;
  email: string;
  phone: string | null;
  role: string;
  status: string;
  last_login_at: string | null;
  created_at: string;
  permissions: string[];
  role_display_name: string;
}

export async function fetchSession(): Promise<AdminSession> {
  const raw = await apiFetch<AdminMeWire>("/api/admin/v1/me", {
    tenantSlug: PLATFORM_TENANT_SLUG,
  });
  // A platform admin's admin_users row has tenant_id = NULL; the
  // UI uses that to decide whether to show the cross-tenant controls.
  const isPlatform = raw.tenant_id === null;
  return {
    admin: {
      id: raw.id,
      email: raw.email,
      display_name: raw.name,
      role: raw.role,
      role_display_name: raw.role_display_name,
      tenant_id: raw.tenant_id,
      // Tenant-scope admins get their tenant's slug + display name
      // from /me (null for platform admins). The landing route uses
      // the slug to send a tenant admin to their console at
      // /tenants/{slug}; the shell uses the name for identity.
      tenant_slug: raw.tenant_slug,
      tenant_name: raw.tenant_name,
      organization_id: raw.organization_id ?? null,
      organization_slug: raw.organization_slug ?? null,
      organization_name: raw.organization_name ?? null,
      permissions: raw.permissions,
      created_at: raw.created_at,
    },
    is_platform_admin: isPlatform,
    // organizations_enabled is a tenant-level feature flag that the
    // /me endpoint does not surface today. Default false; the only
    // place this gates is the sidebar's Organizations link, which we
    // don't expose yet. When the backend adds it, drop the default.
    organizations_enabled: false,
  };
}

// ── Base query hook ──────────────────────────────────────────────────────

export function useSessionQuery(
  opts?: Omit<UseQueryOptions<AdminSession, ApiError>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: sessionQueryKey,
    queryFn: fetchSession,
    // Session is hot data — stay in cache across the tab lifetime.
    // Invalidation happens only on explicit logout or 401.
    staleTime: Infinity,
    ...opts,
  });
}

// ── Admin context hook ───────────────────────────────────────────────────

/**
 * Read the current admin from the TanStack Query cache.
 *
 * This hook assumes the session has already been prefetched by
 * `_authed.tsx` beforeLoad. If the cache is empty it will still
 * trigger a fetch, but that's an unusual path — practically every
 * call site is inside the auth guard.
 *
 * Returns a stable object with the admin + a handful of computed
 * helpers. Components should prefer the helpers over raw permission
 * string matching.
 */
export function useAdmin(): AdminContext {
  const query = useSessionQuery();
  const managed = useRouterState({ select: state => {
    const match = state.matches.find(item => item.routeId === "/_authed/tenants/$slug");
    return (match?.context as { authority?: z.infer<typeof managedTenantContextSchema> } | undefined)?.authority;
  } });
  const liveAuthority = useQuery({ ...tenantContextOptions(managed?.tenant.slug ?? ""), enabled: !!managed,
    refetchInterval: managed && !managed.native ? 30_000 : false,
  });
  if (!query.data) {
    // Unauthenticated branch — only reachable inside `/login` or
    // during the brief window between session invalidation and the
    // auth guard redirect. Callers under `_authed/*` never see this.
    return UNAUTH_CONTEXT;
  }
  const admin = buildAdminContext(query.data);
  if (!managed) return admin;
  const current = liveAuthority.data ?? managed;
  const granted = liveAuthority.isError ? [] : current.permissions;
  const permissions = new Set(granted);
  return { ...admin, can: permission => permissions.has(permission), canWrite: () => granted.some(permission => /\.(write|manage|decide|invite|revoke|delete|activate|certify)$/.test(permission)), tenantSlug: managed.tenant.slug };

}

export interface AdminContext {
  /** The raw admin record from /me, or null when unauthenticated. */
  admin: AdminSession["admin"] | null;
  /** Is the current admin the PureID platform owner (multi-tenant scope)? */
  isPlatformAdmin: boolean;
  /** Tenant slug this admin is scoped to (null for platform admins). */
  tenantSlug: string | null;
  /** Are organizations enabled for this tenant? Drives sidebar/nav. */
  organizationsEnabled: boolean;
  /** Fine-grained permission check. */
  can: (permission: string) => boolean;
  /**
   * Convenience: can the admin perform ANY write operation in the
   * current scope? Maps to role > read-only.
   */
  canWrite: () => boolean;
  /**
   * Does this specific action require a WebAuthn step-up challenge?
   * The server is authoritative and will reject on its own; this is
   * used to render step-up hints in the UI.
   */
  stepUpRequired: (permission: string) => boolean;
}

const UNAUTH_CONTEXT: AdminContext = {
  admin: null,
  isPlatformAdmin: false,
  tenantSlug: null,
  organizationsEnabled: false,
  can: () => false,
  canWrite: () => false,
  stepUpRequired: () => false,
};

function buildAdminContext(session: AdminSession): AdminContext {
  const permissionSet = new Set(session.admin.permissions);
  const stepUpSet = new Set<string>();
  // We don't have the static permission catalog inlined here; the
  // server marks step-up-required actions with a trailing `!` in its
  // permission list, which the frontend UI uses purely for hints.
  // If that convention changes, update it in one place.
  for (const p of session.admin.permissions) {
    if (p.endsWith("!")) stepUpSet.add(p.replace(/!$/, ""));
  }

  return {
    admin: session.admin,
    isPlatformAdmin: session.is_platform_admin,
    tenantSlug: session.admin.tenant_slug,
    organizationsEnabled: session.organizations_enabled,
    can: (permission) => permissionSet.has(permission) || permissionSet.has(`${permission}!`),
    canWrite: () => {
      // Any "*.write" permission means the admin can mutate something.
      for (const p of permissionSet) {
        if (p.endsWith(".write") || p.endsWith(".write!")) return true;
      }
      return false;
    },
    stepUpRequired: (permission) => stepUpSet.has(permission),
  };
}

// ── Cache management ─────────────────────────────────────────────────────

/**
 * Revoke the session, leave the protected shell, then clear its cache.
 */
export function useLogout() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async () => {
    // A server-side failure is recorded, not thrown yet: the local
    // teardown below runs either way, because the user asked to sign
    // out. The throw happens after it, so callers can tell the person
    // their session may still be live instead of showing them a clean
    // sign-out that did not happen.
    let failure: string | null = null;
    try {
      // POST /oauth/logout revokes the server-side session AND
      // clears the session cookie with the correct (domain, path,
      // samesite) tuple. Plain fetch (not apiFetch) because the
      // OAuth endpoint returns text/plain, not JSON.
      //
      // X-Tenant-Slug ensures the backend's tenant resolver
      // identifies the admin tenant even if the Host header
      // resolution fails for any reason.
      const response = await fetch("/oauth/logout", {
        method: "POST",
        credentials: "include",
        // redirect:"manual" because RP-initiated logout answers a
        // browser POST with 302 to the tenant's /login — a DIFFERENT
        // origin than this console. Left on the default "follow", the
        // browser would chase that redirect in cors mode, the portal
        // would not send Access-Control-Allow-Origin for us, and fetch
        // would reject on a sign-out that actually succeeded. The
        // Set-Cookie that clears the session rides on the 302 itself
        // and is applied whether or not we follow it.
        redirect: "manual",
        headers: { "X-Tenant-Slug": PLATFORM_TENANT_SLUG },
      });
      // CHECKING response.ok IS THE LOAD-BEARING PART. fetch rejects
      // only on a transport failure, so a 403 or a 404 resolves
      // normally and an unchecked call reports success for a sign-out
      // that revoked nothing. That is exactly how this broke: the
      // console's POST was answered 403 (origin gate) and, before
      // that, 404 (the localdev proxy never forwarded /oauth/*), while
      // the SPA cleared its cache and navigated to /login with the
      // session cookie still valid. Pressing Back signed the operator
      // back in, and nothing anywhere said so.
      // An unfollowed redirect arrives as an opaque response: type
      // "opaqueredirect", status 0, ok false. That is the SUCCESS path
      // here, so it is checked before ok — a plain `!response.ok`
      // would report every healthy sign-out as a failure. A real
      // refusal (403 origin, 404 route) is a normal response and still
      // falls through.
      if (response.type !== "opaqueredirect" && !response.ok) {
        failure = `The server refused the sign-out (HTTP ${response.status}). Your session may still be active — close the browser before leaving this device.`;
      }
    } catch {
      failure =
        "The sign-out request could not reach the server. Your session may still be active — close the browser before leaving this device.";
    }
    // Cancel old reads before leaving, but retain their cached data until
    // navigation has unmounted the protected shell. Clearing it earlier makes
    // the still-mounted session observers refetch during the router transition
    // with the now-revoked cookie, replacing /login with the 401 error screen.
    await qc.cancelQueries();
    try {
      await navigate({ to: "/login", replace: true });
    } finally {
      qc.clear();
    }
    if (failure) {
      throw new Error(failure);
    }
  };
}
